import { Link } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import AddIcon from "@mui/icons-material/Add";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import DatePicker from "@mui/lab/DatePicker";
import LocalizationProvider from "@mui/lab/LocalizationProvider";
import { Box, Tooltip, Typography } from "@mui/material";
import TextField from "@mui/material/TextField";
import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router";
import {
  deleteService,
  getJsonResult,
  post,
} from "../../services/FetchServices";
import OrderRelease from "../../types/OrderRelease";
import SearchOrderRelease from "../../types/SearchOrderRelease";
import { DropDown } from "../dropDown/DropDown";
import Report from "../Report";

import {
  getOrderReleaseReportData,
  getOrderReleaseReportFixedHeaders,
  getReportFileName,
  subtractOneDayFrom,
} from "./OrderReleaseReportHelper";

interface FindOrderReleaseProps {
  searchOrderRelease: SearchOrderRelease;
}

const SearchOrderReleasePage: React.FC = () => {
  const location = useLocation();
  const history = useHistory();
  const [searchOrderRelease, setSearchOrderRelease] = useState<
    SearchOrderRelease | undefined
  >((location?.state as FindOrderReleaseProps)?.searchOrderRelease);
  const [open, setOpen] = useState(false);
  const [openAlert, setAlertOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [orderReleaseLoading, setIsOrderReleaseLoading] = useState(false);
  const [orderReleaseId, setOrderReleaseId] = useState("");
  const [purchaseOrderId, setPurchaseOrderId] = useState("");
  const [shipmentId, setShipmentId] = useState("");
  const [freightTerms, setFreightTerms] = useState("");
  const [appointmentId, setAppointmentId] = useState("");
  const [earlyPickupOnOrAfter, setEarlyPickupOnOrAfter] = useState<
    string | null
  >(null);
  const [earlyPickupOnOrBefore, setEarlyPickupOnOrBefore] = useState<
    string | null
  >(null);
  const [latePickupOnOrAfter, setLatePickupOnOrAfter] = useState<string | null>(
    null,
  );
  const [latePickupOnOrBefore, setLatePickupOnOrBefore] = useState<
    string | null
  >(null);
  const [earlyDeliveryOnOrAfter, setEarlyDeliveryOnOrAfter] = useState<
    string | null
  >(null);
  const [earlyDeliveryOnOrBefore, setEarlyDeliveryOnOrBefore] = useState<
    string | null
  >(null);
  const [lateDeliveryOnOrAfter, setLateDeliveryOnOrAfter] = useState<
    string | null
  >(null);
  const [lateDeliveryOnOrBefore, setLateDeliveryOnOrBefore] = useState<
    string | null
  >(null);
  const [poCreationOnOrAfter, setPoCreationOnOrAfter] = useState<string | null>(
    null,
  );
  const [poCreationOnOrBefore, setPoCreationOnOrBefore] = useState<
    string | null
  >(null);
  const [destinationDc, setDestinationDc] = useState("");
  const [orderReleases, setOrderReleases] = useState<OrderRelease[]>([]);
  const [dcOptions, setDcOptions] = useState<string[]>([]);
  const [dialogContent, setDialogContent] = useState("");
  const [dialogTitle, setDialogTitle] = useState("");
  const [orderReleaseIdToDelete, setOrderReleaseIdToDelete] = useState("");
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});

  const routeToEditOrderReleaseDetailsPage = async (
    orderReleaseId: string,
    editable: boolean,
  ) => {
    setIsOrderReleaseLoading(true);
    try {
      const findLocationUrl =
        `${process.env.REACT_APP_BACKEND_URL}/matm/findLocation/search?vendorId=` +
        localStorage.getItem("vendorId");
      const locationResponse = await post(findLocationUrl, {
        activeStatus: "Y",
      });
      const locationResponseData = await locationResponse.json();

      const orderReleaseDetailsUrl = `${process.env.REACT_APP_BACKEND_URL}/api/orderRelease/details/${orderReleaseId}`;
      const hazmatCodesUrl = `${process.env.REACT_APP_BACKEND_URL}/api/itemMaster/hazmatCodes`;

      const hazmatCodesResponse = await getJsonResult(hazmatCodesUrl);

      const orderReleaseResponse = await getJsonResult(orderReleaseDetailsUrl);

      const hazmatCodes = await hazmatCodesResponse.json();
      const orderReleaseData = await orderReleaseResponse.json();

      let searchOrderReleaseRequest = new SearchOrderRelease(
        orderReleaseId,
        purchaseOrderId,
        earlyPickupOnOrAfter,
        earlyPickupOnOrBefore,
        latePickupOnOrAfter,
        latePickupOnOrBefore,
        destinationDc,
        freightTerms,
        shipmentId,
        poCreationOnOrAfter,
        poCreationOnOrBefore,
        earlyDeliveryOnOrAfter,
        earlyDeliveryOnOrBefore,
        lateDeliveryOnOrAfter,
        lateDeliveryOnOrBefore,
        appointmentId,
      );
      history.push({
        pathname: "/OrderReleaseDetails",
        state: {
          purchaseOrderRelease: orderReleaseData,
          hazmatCodes: hazmatCodes,
          locations: locationResponseData,
          searchOrderRelease: searchOrderReleaseRequest,
          isPageEditable: editable,
          navigatedBack: false,
          navigatedFromOrderReleaseSearch: true,
        },
      });
    } catch (error) {
      console.error(
        "Process failed unable to fetch order release details:",
        error,
      );
    } finally {
      setIsOrderReleaseLoading(false);
    }
  };

  const findOrderRelease = () => {
    let orderReleaseRequest = {};
    if (searchOrderRelease !== undefined) {
      orderReleaseRequest = {
        orderReleaseId: searchOrderRelease?.orderReleaseId,
        purchaseOrderId: searchOrderRelease?.purchaseOrderId,
        shipmentId: searchOrderRelease?.shipmentId,
        freightTerms: searchOrderRelease?.freightTerms,
        appointmentId: searchOrderRelease?.appointmentId,
        earlyPickupOnOrAfter: searchOrderRelease?.earlyPickupDate,
        earlyPickupOnOrBefore: searchOrderRelease?.earlyPickupDateBefore,
        latePickupOnOrAfter: searchOrderRelease?.latePickupDate,
        latePickupOnOrBefore: searchOrderRelease?.latePickupDateBefore,
        earlyDeliveryOnOrAfter: searchOrderRelease?.earlyDeliveryDate,
        earlyDeliveryOnOrBefore: searchOrderRelease?.earlyDeliveryDateBefore,
        lateDeliveryOnOrAfter: searchOrderRelease?.lateDeliveryDate,
        lateDeliveryOnOrBefore: searchOrderRelease?.lateDeliveryDateBefore,
        poCreationOnOrAfter: searchOrderRelease?.poCreationDate,
        poCreationOnOrBefore: searchOrderRelease?.poCreationDateBefore,
        destinationDc: searchOrderRelease?.shipTo,
      };
      setSearchOrderRelease(undefined);
    } else {
      orderReleaseRequest = {
        orderReleaseId: orderReleaseId,
        purchaseOrderId: purchaseOrderId,
        shipmentId: shipmentId,
        freightTerms: freightTerms,
        appointmentId: appointmentId,
        earlyPickupOnOrAfter: earlyPickupOnOrAfter,
        earlyPickupOnOrBefore: earlyPickupOnOrBefore,
        latePickupOnOrAfter: latePickupOnOrAfter,
        latePickupOnOrBefore: latePickupOnOrBefore,
        earlyDeliveryOnOrAfter: earlyDeliveryOnOrAfter,
        earlyDeliveryOnOrBefore: earlyDeliveryOnOrBefore,
        lateDeliveryOnOrAfter: lateDeliveryOnOrAfter,
        lateDeliveryOnOrBefore: lateDeliveryOnOrBefore,
        poCreationOnOrAfter: poCreationOnOrAfter,
        poCreationOnOrBefore: poCreationOnOrBefore,
        destinationDc: destinationDc,
      };
    }
    setIsLoading(true);
    const orderReleaseUrl = `${process.env.REACT_APP_BACKEND_URL}/api/orderRelease/search`;
    post(orderReleaseUrl, orderReleaseRequest)
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            "Error occured while getting the order release result from the search criteria.",
          );
        }
        return response.json();
      })
      .then((data: OrderRelease[]) => {
        if (data == null || data.length === 0) {
          setOrderReleases([]);
          setAlertOpen(true);
          setDialogContent("No results returned from the search criteria.");
          setDialogTitle("Warning!");
        }
        setOrderReleases(data);
      })
      .catch((error: Error) => {
        setAlertOpen(true);
        setDialogTitle("Error!");
        setDialogContent(error?.message);
        setOrderReleases([]);
      })
      .finally(() => {
        setIsLoading(false);
        if (orderReleases?.length !== 0) {
          fixOrderReleaseTableHeaderWidth();
        }
      });
  };

  const closeAlertDialog = () => {
    setAlertOpen(false);
  };

  const getDcList = () => {
    setIsLoading(true);
    const orderReleaseUrl = `${process.env.REACT_APP_BACKEND_URL}/matm/dcCity/list`;

    getJsonResult(orderReleaseUrl)
      .then((response) => response.json())
      .then((data) => setDcOptions(data))
      .finally(() => {
        setIsLoading(false);
      });
  };

  const base64ToBlob = (base64: string): Blob => {
    const byteCharacters: string = atob(base64);
    const byteArrays: Uint8Array[] = [];

    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice: string = byteCharacters.slice(offset, offset + 512);
      const byteNumbers: number[] = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      const byteArray: Uint8Array = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, { type: "application/pdf" });
  };

  useEffect(() => {
    getDcList();

    if (searchOrderRelease !== undefined) {
      setOrderReleaseId(searchOrderRelease?.orderReleaseId);
      setAppointmentId(searchOrderRelease?.appointmentId);
      setFreightTerms(searchOrderRelease?.freightTerms);
      setDestinationDc(searchOrderRelease?.shipTo);
      setEarlyDeliveryOnOrAfter(searchOrderRelease?.earlyDeliveryDate);
      setEarlyDeliveryOnOrBefore(searchOrderRelease?.earlyDeliveryDateBefore);
      setEarlyPickupOnOrAfter(searchOrderRelease?.earlyPickupDate);
      setEarlyPickupOnOrBefore(searchOrderRelease?.earlyPickupDateBefore);
      setLateDeliveryOnOrAfter(searchOrderRelease?.lateDeliveryDate);
      setLateDeliveryOnOrBefore(searchOrderRelease?.lateDeliveryDateBefore);
      setLatePickupOnOrAfter(searchOrderRelease?.latePickupDate);
      setLatePickupOnOrBefore(searchOrderRelease?.latePickupDateBefore);
      setPoCreationOnOrAfter(searchOrderRelease?.poCreationDate);
      setPoCreationOnOrBefore(searchOrderRelease?.poCreationDateBefore);
      setShipmentId(searchOrderRelease?.shipmentId);

      findOrderRelease();
    }
  }, []);

  const onKeyDown = (event: any) => {
    if (event.keyCode === 13) {
      findOrderRelease();
      event.preventDefault();
    }
  };

  const fixOrderReleaseTableHeaderWidth = () => {
    var $table = $("#orderReleaseTable");
    var $bodyCells = $table?.find("tbody tr:first").children();

    var colWidth = $bodyCells
      ?.map(function () {
        return $(this).width();
      })
      ?.get();

    $table
      ?.find("thead tr")
      ?.children()
      ?.each(function (i, v) {
        $(v).width(colWidth[i]);
      });
  };

  const openDialog = (orderReleaseId: string) => {
    setOrderReleaseIdToDelete(orderReleaseId);
    setOpen(true);
  };

  const closeDialog = () => {
    setOpen(false);
  };

  const deleteOrder = () => {
    closeDialog();
    var deleteLocationUrl =
      `${process.env.REACT_APP_BACKEND_URL}/api/orderRelease/deleteOrder?orderReleaseId=` +
      orderReleaseIdToDelete;
    deleteService(deleteLocationUrl)
      .then((response) => {
        if (response.status >= 200 && response.status <= 299) {
          return response.text();
        } else {
          throw response;
        }
      })
      .then((data) => {
        setDialogContent("Order successfully deleted");
        setDialogTitle("Success!");
        setAlertOpen(true);
        findOrderRelease();
        setIsLoading(false);
      })
      .catch((error) => {
        error.text().then((errMessage: any) => {
          setDialogContent("Error when deleting order:\n" + errMessage);
          setDialogTitle("Error!");
          setAlertOpen(true);
        });
      });
  };

  const toggleColumn = (index: number) => {
    setExpandedRows((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  return orderReleaseLoading ? (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 9999,
      }}
    >
      <div className="bg-white p-4 rounded shadow-lg text-center">
        <div className="d-flex justify-content-center mb-2">
          <output
            className="spinner-border text-primary"
            id="fetchOrderReleaseSpinner"
          ></output>
        </div>
        <strong>Fetching Order...</strong>
      </div>
    </div>
  ) : (
    <section
      className="sectionHeadingPadding"
      onKeyDown={onKeyDown}
      tabIndex={-1}
      style={{ outline: "none" }}
    >
      <div id="searchOrderReleasePage" className="container">
        <div id="findOrderReleasePage" className="intro">
          <div id="orderReleaseFinder" className="section-heading mb-4">
            <span
              className="section-heading-lower"
              style={{
                fontSize: "1.9rem",
                color: "#6b3a06",
                textAlign: "center",
              }}
            >
              SUPPLIER ROUTING REQUEST (SRR) FINDER
            </span>
          </div>

          <div className="row mb-5">
            <div className="col-lg-12 mx-auto">
              <div
                className="bg-grey p-5 rounded"
                style={{ boxShadow: " 0px 5px 10px #91d475" }}
              >
                <div className="row">
                  <div className="col-md">
                    <div className="form-group py-2">
                      <TextField
                        id="orderReleaseId"
                        label="Supplier Routing Request ID"
                        variant="outlined"
                        className="form-control"
                        value={orderReleaseId}
                        onChange={(e) => setOrderReleaseId(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-md">
                    <div className="form-group py-2">
                      <TextField
                        id="purchaseOrderId"
                        label="Purchase Order ID"
                        variant="outlined"
                        className="form-control"
                        value={purchaseOrderId}
                        onChange={(e) => setPurchaseOrderId(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-md">
                    <div className="form-group py-2">
                      <TextField
                        id="shipmentId"
                        label="Shipment ID"
                        variant="outlined"
                        className="form-control"
                        value={shipmentId}
                        onChange={(e) => setShipmentId(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-md">
                    <div className="form-group py-2">
                      <DropDown
                        codes={["PREPAID", "COLLECT"]}
                        elementId="freightTerms"
                        value={freightTerms}
                        onChange={(e: React.ChangeEvent<{ value: unknown }>) =>
                          setFreightTerms(e.target.value as string)
                        }
                        label="Freight Terms"
                        defaultOption="None"
                      />
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md">
                    <div className="form-group py-2">
                      <TextField
                        id="appointmentId"
                        label="Delivery Appointment ID"
                        variant="outlined"
                        className="form-control"
                        value={appointmentId}
                        onChange={(e) => setAppointmentId(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-md">
                    <div className="form-group py-2">
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          label="Early Product Ready on or after"
                          views={["year", "month", "day"]}
                          value={earlyPickupOnOrAfter}
                          onChange={(date) =>
                            setEarlyPickupOnOrAfter(
                              date ? new Date(date).toLocaleDateString() : null,
                            )
                          }
                          renderInput={(params: any) => (
                            <TextField
                              {...params}
                              id="earlyPickupOnOrAfter"
                              className="form-control"
                              helperText={null}
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </div>
                  </div>
                  <div className="col-md">
                    <div className="form-group py-2">
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          label="Early Product Ready on or before"
                          views={["year", "month", "day"]}
                          value={earlyPickupOnOrBefore}
                          onChange={(date) =>
                            setEarlyPickupOnOrBefore(
                              date ? new Date(date).toLocaleDateString() : "",
                            )
                          }
                          renderInput={(params: any) => (
                            <TextField
                              {...params}
                              id="earlyPickupOnOrBefore"
                              className="form-control"
                              helperText={null}
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </div>
                  </div>
                  <div className="col-md">
                    <div className="form-group py-2">
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          label="Late Product Ready on or after"
                          views={["year", "month", "day"]}
                          value={latePickupOnOrAfter}
                          onChange={(date) =>
                            setLatePickupOnOrAfter(
                              date ? new Date(date).toLocaleDateString() : "",
                            )
                          }
                          renderInput={(params: any) => (
                            <TextField
                              {...params}
                              id="latePickupOnOrAfter"
                              className="form-control"
                              helperText={null}
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md">
                    <div className="form-group py-2">
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          label="Late Product Ready on or before"
                          views={["year", "month", "day"]}
                          value={latePickupOnOrBefore}
                          onChange={(date) =>
                            setLatePickupOnOrBefore(
                              date ? new Date(date).toLocaleDateString() : "",
                            )
                          }
                          renderInput={(params: any) => (
                            <TextField
                              {...params}
                              id="latePickupOnOrBefore"
                              className="form-control"
                              helperText={null}
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </div>
                  </div>
                  <div className="col-md">
                    <div className="form-group py-2">
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          label="Early Delivery On or After"
                          views={["year", "month", "day"]}
                          value={earlyDeliveryOnOrAfter}
                          onChange={(date) =>
                            setEarlyDeliveryOnOrAfter(
                              date ? new Date(date).toLocaleDateString() : "",
                            )
                          }
                          renderInput={(params: any) => (
                            <TextField
                              {...params}
                              id="earlyDeliveryOnOrAfter"
                              className="form-control"
                              helperText={null}
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </div>
                  </div>
                  <div className="col-md">
                    <div className="form-group py-2">
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          label="Early Delivery On or Before"
                          views={["year", "month", "day"]}
                          value={earlyDeliveryOnOrBefore}
                          onChange={(date) =>
                            setEarlyDeliveryOnOrBefore(
                              date ? new Date(date).toLocaleDateString() : "",
                            )
                          }
                          renderInput={(params: any) => (
                            <TextField
                              {...params}
                              id="earlyDeliveryOnOrBefore"
                              className="form-control"
                              helperText={null}
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </div>
                  </div>
                  <div className="col-md">
                    <div className="form-group py-2">
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          label="Late Delivery On or After"
                          views={["year", "month", "day"]}
                          value={lateDeliveryOnOrAfter}
                          onChange={(date) =>
                            setLateDeliveryOnOrAfter(
                              date ? new Date(date).toLocaleDateString() : "",
                            )
                          }
                          renderInput={(params: any) => (
                            <TextField
                              {...params}
                              id="lateDeliveryOnOrAfter"
                              className="form-control"
                              helperText={null}
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md">
                    <div className="form-group py-2">
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          label="Late Delivery On or Before"
                          views={["year", "month", "day"]}
                          value={lateDeliveryOnOrBefore}
                          onChange={(date) =>
                            setLateDeliveryOnOrBefore(
                              date ? new Date(date).toLocaleDateString() : "",
                            )
                          }
                          renderInput={(params: any) => (
                            <TextField
                              {...params}
                              id="lateDeliveryOnOrBefore"
                              className="form-control"
                              helperText={null}
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </div>
                  </div>
                  <div className="col-md">
                    <div className="form-group py-2">
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          label="SRR Creation On or After"
                          views={["year", "month", "day"]}
                          value={poCreationOnOrAfter}
                          onChange={(date) =>
                            setPoCreationOnOrAfter(
                              date ? new Date(date).toLocaleDateString() : "",
                            )
                          }
                          renderInput={(params: any) => (
                            <TextField
                              {...params}
                              id="poCreationOnOrAfter"
                              className="form-control"
                              helperText={null}
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </div>
                  </div>
                  <div className="col-md">
                    <div className="form-group py-2">
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          label="SRR Creation On or Before"
                          views={["year", "month", "day"]}
                          value={poCreationOnOrBefore}
                          onChange={(date) =>
                            setPoCreationOnOrBefore(
                              date ? new Date(date).toLocaleDateString() : "",
                            )
                          }
                          renderInput={(params: any) => (
                            <TextField
                              {...params}
                              id="poCreationOnOrBefore"
                              className="form-control"
                              helperText={null}
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </div>
                  </div>
                  <div className="col-md">
                    <div className="form-group py-2">
                      <DropDown
                        codes={dcOptions}
                        elementId="destinationDc"
                        value={destinationDc}
                        onChange={(e: React.ChangeEvent<{ value: unknown }>) =>
                          setDestinationDc(e.target.value as string)
                        }
                        label="DC# CITY STATE"
                        defaultOption="ALL"
                      />
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div style={{ textAlign: "center" }}>
                    <span>
                      <button
                        type="button"
                        className="btn btn-success"
                        id="searchOrderReleaseButton"
                        onClick={findOrderRelease}
                        style={{
                          width: 100,
                          marginBottom: "10px",
                        }}
                      >
                        Find
                      </button>
                    </span>
                    &nbsp;
                    {orderReleases?.length > 0 ? (
                      <span>
                        <Report
                          id="orderReleaseReport"
                          headers={getOrderReleaseReportFixedHeaders()}
                          data={getOrderReleaseReportData(orderReleases)}
                          filename={getReportFileName("Order_Release_Report")}
                          title="Supplier Routing Request"
                        >
                          <button
                            id="exportOrderReleaseButton"
                            type="button"
                            className="btn btn-success"
                            style={{
                              width: 100,
                              marginBottom: "10px",
                            }}
                          >
                            Export
                          </button>
                        </Report>
                      </span>
                    ) : (
                      ""
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div id="orderReleaseTableDiv" className="overflowX">
          {isLoading ? (
            <div className="d-flex justify-content-center">
              <output
                className="spinner-border text-primary"
                id="orderReleaseSpinner"
              ></output>
            </div>
          ) : (
            <table
              className="table"
              id="orderReleaseTable"
              style={{ margin: "0 auto", borderCollapse: "collapse" }}
            >
              <thead>
                <tr>
                  <th>Supplier Routing Request ID</th>
                  <th>Purchase Order ID</th>
                  <th>Release Status</th>
                  <th>Early Product Ready</th>
                  <th>Late Product Ready</th>
                  <th>Ship From</th>
                  <th>Ship To</th>
                  <th>Freight Terms</th>
                  <th>Shipment Status</th>
                  <th>SCAC</th>
                  <th>Delivery Appointment ID</th>
                  <th>Vendor Pickup Number</th>
                  <th>Shipment ID/BOL</th>
                  <th></th>
                  <th></th>
                </tr>
              </thead>
              <tbody
                style={{
                  overflowY: "scroll",
                  maxHeight: "650px",
                }}
              >
                {orderReleases?.map((orderRelease: OrderRelease, index) => (
                  <tr key={orderRelease.orderReleaseId}>
                    <td className="nowrap">
                      <Link
                        onClick={() =>
                          routeToEditOrderReleaseDetailsPage(
                            orderRelease?.orderReleaseId,
                            false,
                          )
                        }
                      >
                        {orderRelease.orderReleaseId.startsWith("FD.")
                          ? orderRelease.orderReleaseId.slice(3)
                          : orderRelease.orderReleaseId}
                      </Link>
                    </td>
                    {/* <td title={""}>{orderRelease?.purchaseOrderIds?.join(", ")}</td> */}
                    <td key={index}>
                      {orderRelease?.purchaseOrderIds?.length > 1 ? (
                        <Box display="flex" flexDirection="column">
                          <Tooltip
                            disableFocusListener
                            disableTouchListener
                            title={
                              <div style={{ whiteSpace: "pre-line" }}>
                                {orderRelease?.purchaseOrderIds?.map(
                                  (poId, i) => (
                                    <div key={i}>
                                      {poId}
                                      {i <
                                      orderRelease?.purchaseOrderIds?.length - 1
                                        ? ", "
                                        : ""}
                                    </div>
                                  ),
                                )}
                              </div>
                            }
                          >
                            <span>
                              <u>{orderRelease?.purchaseOrderIds?.[0]}</u>
                              &nbsp;&nbsp;
                              <AddIcon />
                            </span>
                          </Tooltip>
                        </Box>
                      ) : (
                        <Typography>
                          {orderRelease?.purchaseOrderIds}
                        </Typography>
                      )}
                    </td>
                    <td title={""}>{orderRelease.orderReleaseStatus}</td>
                    <td>{orderRelease?.earlyPickupDate?.split("T")[0]}</td>
                    <td>
                      {
                        subtractOneDayFrom(orderRelease?.latePickupDate)?.split(
                          "T",
                        )[0]
                      }
                    </td>
                    <td>{orderRelease.shipFromName}</td>
                    <td>{orderRelease.shipToName}</td>
                    <td>{orderRelease.freightTerms}</td>
                    <td>{orderRelease?.shipmentStatus}</td>
                    <td>
                      {orderRelease?.shipmentStatus !==
                        "Awaiting Shipment Planning" && orderRelease?.scac
                        ? orderRelease.scac
                        : "No data to display"}
                    </td>
                    <td>
                      {orderRelease.appointmentId
                        ? orderRelease.appointmentId
                        : "No data to display"}
                    </td>
                    <td>
                      {orderRelease?.vendorPickupNumber
                        ? orderRelease?.vendorPickupNumber
                        : "No data to display"}
                    </td>
                    <td>{renderShipmentBol(orderRelease)}</td>
                    <td>
                      {orderRelease.deleteOrderRelease && (
                        <button
                          id="deleteOrderButton"
                          name={
                            orderRelease.orderReleaseId +
                            "deleteOrderButtonName"
                          }
                          className="btn btn-danger"
                          onClick={() =>
                            openDialog(orderRelease.orderReleaseId)
                          }
                        >
                          Delete
                        </button>
                      )}
                    </td>
                    <td>
                      {orderRelease.editable && (
                        <button
                          id="editOrderButton"
                          name={
                            orderRelease.orderReleaseId + "editOrderButtonName"
                          }
                          className="btn btn-dark"
                          onClick={() =>
                            routeToEditOrderReleaseDetailsPage(
                              orderRelease?.orderReleaseId,
                              true,
                            )
                          }
                        >
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      <div id="deleteDialog">
        <Dialog open={open} onClose={closeDialog}>
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              id="searchLocationButton"
              onClick={closeDialog}
              color="primary"
              autoFocus
            >
              No
            </Button>
            <Button
              id="searchLocationButton"
              onClick={deleteOrder}
              color="primary"
              autoFocus
            >
              Yes
            </Button>
          </DialogActions>
        </Dialog>
      </div>
      <div id="alertDialog">
        <Dialog open={openAlert} id="dialog">
          <DialogTitle id="alertDialogTitle">{dialogTitle}</DialogTitle>
          <DialogContent>
            <DialogContentText id="alertDialogText">
              {dialogContent}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              id="alertDialogButton"
              onClick={closeAlertDialog}
              color="primary"
              autoFocus
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </section>
  );

  function openPdfdocument(
    orderRelease: OrderRelease,
  ): React.MouseEventHandler<HTMLAnchorElement> | undefined {
    return (e) => {
      e.preventDefault();
      getJsonResult(
        `${process.env.REACT_APP_BACKEND_URL}/api/orderRelease/view/bolReport/shipment/${orderRelease?.shipmentId}/facility/${orderRelease?.shipFrom}`,
      )
        .then((response) => {
          if (response.ok) {
            return response.json();
          } else {
            setDialogContent("Error fetching bol report.");
            setDialogTitle("Error!");
            setAlertOpen(true);
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
        })
        .then((data) => {
          if (data?.bolDocument) {
            const pdfBlob: Blob = base64ToBlob(data?.bolDocument);
            const blobUrl: string = URL.createObjectURL(pdfBlob);
            const newWindow = window.open(blobUrl, "_blank");

            if (newWindow) {
              const interval = setInterval(() => {
                if (newWindow.closed) {
                  URL.revokeObjectURL(blobUrl);
                  console.log("PDF Blob URL revoked.");
                  clearInterval(interval);
                }
              }, 1000);
            } else {
              setDialogContent(
                "The browser blocked the popup. Please allow popups for this site.",
              );
              setDialogTitle("Warning!");
              setAlertOpen(true);
            }
          } else {
            setDialogContent("The requested document was not found.");
            setDialogTitle("Warning!");
            setAlertOpen(true);
          }
        })
        .catch((error) => {
          console.log("Error while rendering pdf document.");
        });
    };
  }

  function renderShipmentBol(orderRelease: OrderRelease) {
    if (orderRelease?.shipmentId && orderRelease.tenderStatus === "2900") {
      return (
        <a
          id={orderRelease?.shipmentId}
          href="#"
          title="Open report"
          onClick={openPdfdocument(orderRelease)}
        >
          {orderRelease?.shipmentId}
        </a>
      );
    } else if (
      orderRelease?.shipmentId &&
      orderRelease.tenderStatus !== "2900"
    ) {
      return (
        <div id={orderRelease?.shipmentId} title="Tender not accepted">
          {orderRelease?.shipmentId}
        </div>
      );
    } else {
      return "No data to display";
    }
  }
};

export default SearchOrderReleasePage;
